import { initializeApp } from "firebase/app";
import {
    GoogleAuthProvider,
    getAuth,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInAnonymously,
    sendPasswordResetEmail,
    signOut,
} from "firebase/auth";
import {
    getFirestore,
    query,
    collection,
    collectionGroup,
    where,
    orderBy,
    addDoc,
    doc,
    getDoc,
    getDocs,
    deleteDoc,
    setDoc,
    connectFirestoreEmulator,
    onSnapshot,
    documentId,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
    UserMaster,
    UserWizardObj,
    ContactMaster,
    ContactDescription,
    ContactTag,
} from "./types";
import moment from "moment";

import { CONTACT_MASTER, CONTACT_DESCRIPTION } from "./default-types";
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DB_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
export const db = getFirestore(app);
const functions = getFunctions(app);
export const storage = getStorage(app);

export const createGoogleUser = async (
    email: string,
    googleId: string,
    name: string
): UserMaster => {
    const created_date = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");
    const user = {
        created_date: created_date,
        updated_date: created_date,
        device_type: "web",

        email: email,
        google_id: googleId,
        name: name,
        startTrialDate: created_date,
        numberOfTrialDays: 30,
        purchase_name: "Meet and Greet",
        profile: "",
        send_emails: true,
        send_notifications: true,
        seconds_from_gmt: new Date().getTimezoneOffset() * -60, // user timezone in seconds
        notifTimePreferred: "7#00#AM",
        is_keep_in_touch_email: "1",
        numberOfRandomFacts: "5",
        numberOfLocationFacts: "2",
    };
    const newUserRef = await addDoc(collection(db, "user_master"), user);
    const updateUserId = await setDoc(
        doc(db, "user_master", newUserRef.id),
        { user_id: newUserRef.id },
        { merge: true }
    );
    user.user_id = newUserRef.id;
    createEmptyTag("Family", user.user_id);
    createEmptyTag("Friends", user.user_id);
    createEmptyTag("Professional", user.user_id);
    return user;
};

export const createNewUser = async (
    email: string,
    userId: string,
    name: string
): UserMaster => {
    const created_date = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");
    const user = {
        created_date: created_date,
        updated_date: created_date,
        device_type: "web",

        email: email,
        user_id: userId,
        google_id: userId,
        name: name,
        startTrialDate: created_date,
        numberOfTrialDays: 30,
        purchase_name: "Meet and Greet",
        profile: "",
        send_emails: true,
        send_notifications: true,
        seconds_from_gmt: new Date().getTimezoneOffset() * -60, // user timezone in seconds
        notifTimePreferred: "7#00#AM",
        is_keep_in_touch_email: "1",
        numberOfRandomFacts: "5",
        numberOfLocationFacts: "2",
    };
    const newUserRef = await setDoc(doc(db, "user_master", userId), user);
    createEmptyTag("Family", userId);
    createEmptyTag("Friends", userId);
    createEmptyTag("Professional", userId);
    return user;
};

export const fetchCheckoutSession = async (checkoutSessionId: string) => {
    const data = await fetch(
        `https://us-central1-networknerd-4aa62.cloudfunctions.net/updatePurchasePlanByStripeCheckout/${checkoutSessionId}`
    );
    const a = await data.json();
    return a;
};

export const saveUserWizard = async (
    userId: string,
    userWizard: UserWizardObj
) => {
    const created_date = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");
    const userUpdate = {
        updated_date: created_date,
        send_emails: userWizard.emailNotifications,
        send_notifications: userWizard.mobileNotifications,
    };
    const userRef = await setDoc(doc(db, "user_master", userId), userUpdate, {
        merge: true,
    });
    const tags = userWizard.initialContacts
        .map((contact) => contact.tags)
        .reduce((acc, val) => [...acc, ...val], [])
        .filter((a, index, arr) => arr.indexOf(a) === index);

    if (userWizard.initialContacts.length === 0) {
        return;
    }

    const tagsQuery = query(
        collection(db, "tags"),
        where("userID", "==", userId),
        where("description", "in", tags)
    );
    const tagsSnapshot = await getDocs(tagsQuery);
    const tagsData = tagsSnapshot.docs.map((x) => ({
        ...x.data(),
        tag_id: x.id,
    }));

    for (
        let contactIndex = 0;
        contactIndex < userWizard.initialContacts.length;
        contactIndex++
    ) {
        const contact = userWizard.initialContacts[contactIndex];
        const c = await createEmptyContact({
            name: contact.name,
            keepintouch: contact.reminder,
            user_id: userId,
        });
        for (let tagIndex = 0; tagIndex < contact.tags.length; tagIndex++) {
            const tag = tagsData.find(
                (t: ContactTag) => t.description === contact.tags[tagIndex]
            );
            if (!tag) continue;
            await addTagToContact(tag.tag_id, c.contact_id, userId);
        }
    }
};

export const signUpEmailAndPassword = async (
    email: string,
    password: string
) => {
    return await createUserWithEmailAndPassword(auth, email, password);
};

export const signInEmailAndPassword = async (data: {
    email: string,
    password: string,
}) => {
    const userCredentials = signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
    );
    return userCredentials;
};

export const getContacts = async (userId: string): ContactMaster[] => {
    const contactsSnap = await getDocs(
        query(collection(db, "contact_master"), where("user_id", "==", userId))
    );
    return contactsSnap.docs.map((contact) => ({
        ...contact.data(),
        contact_id: contact.id,
    }));
};

const getSharedProfiles = async (contacts: ContactMaster[]) => {
    const sharedContacts = [];
    for (
        let batchIndex = 0;
        batchIndex < Math.floor((contacts.length + 9) / 10);
        batchIndex++
    ) {
        const batchIds = contacts
            .filter(
                (c: ContactMaster, index: number) =>
                    Math.floor(index / 10) === batchIndex
            )
            .map((contact: ContactMaster) => contact.original_person_id);
        const batchQuery = query(
            collection(db, "contact_master"),
            where(documentId(), "in", batchIds)
        );
        const batchContacts = await getDocs(batchQuery);
        const batchData = batchContacts.docs.map((contact) => ({
            ...contact.data(),
            contact_id: contact.id,
        }));
        sharedContacts.push(...batchData);
    }
    return sharedContacts;
};

export const getContactsListener = (
    userId: string,
    callback: (contacts: ContactMaster[]) => void
): (() => void) => {
    const contactsQuery = query(
        collection(db, "contact_master"),
        where("user_id", "==", userId)
    );
    const unsubscribe = onSnapshot(contactsQuery, async (contactsSnap) => {
        const contacts = contactsSnap.docs.map((contact) => ({
            ...contact.data(),
            contact_id: contact.id,
        }));
        callback(contacts);
    });
    return unsubscribe;
};

export const getUsersById = async (userIds: string[]): UserMaster => {
    const users = [];
    for (
        let batchIndex = 0;
        batchIndex < Math.floor((userIds.length + 9) / 10);
        batchIndex++
    ) {
        const batchIds = userIds.filter(
            (id: string, index: number) => Math.floor(index / 10) === batchIndex
        );
        const batchQuery = query(
            collection(db, "user_master"),
            where(documentId(), "in", batchIds)
        );
        const batchUsers = await getDocs(batchQuery);
        const batchData = batchUsers.docs.map((user) => ({
            ...user.data(),
            user_id: user.id,
        }));
        users.push(...batchData);
    }
    return users;
};

export const getProfileFollowersListener = (
    userId: string,
    callback: (users: UserMaster[]) => void
): (() => void) => {
    const peerSharingQuery = query(
        collection(db, "peer_sharing"),
        where("owner_id", "==", userId)
    );
    const unsubscribe = onSnapshot(peerSharingQuery, async (peerSharing) => {
        const peerSharingData = peerSharing.docs.map((user) => ({
            ...user.data(),
        }));
        const users = await getUsersById(
            peerSharingData.map((peer) => peer.recipient_id)
        );
        callback(users);
    });
    return unsubscribe;
};

export const getFactsByUserListener = (
    userId: string,
    callback: (facts: ContactDescription[]) => void
): (() => void) => {
    const factsQuery = query(
        collection(db, "contact_description_master"),
        where("user_id", "==", userId)
    );
    const unsubscribe = onSnapshot(factsQuery, (factsSnap) => {
        const facts = factsSnap.docs
            .map((fact) => ({
                ...fact.data(),
                desc_id: fact.id,
            }))
            .sort((a, b) => {
                if (a.updated_date === b.updated_date) return 0;
                if (a.updated_date > b.updated_date) return 1;
                return -1;
            });
        callback(facts);
    });
    return unsubscribe;
};

export const getTagsByUserListener = (
    userId: string,
    callback: (tags: ContactTag[]) => void
): (() => void) => {
    const tagsQuery = query(
        collection(db, "tags"),
        where("userID", "==", userId)
    );
    const unsubscribe = onSnapshot(tagsQuery, (tagsSnap) => {
        const tags = tagsSnap.docs.map((tag) => ({
            ...tag.data(),
            tag_id: tag.id,
        }));
        callback(tags);
    });
    return unsubscribe;
};

export const getCurrentUser = async (userId: string): Promise<UserMaster> => {
    await signInAnonymously(auth);
    const currentUserQuery = query(
        collection(db, "user_master"),
        where("google_id", "==", userId)
    );
    const users = await getDocs(currentUserQuery);

    if (users.size === 0) {
        throw new Error("No user found");
    } else if (users.docs.size > 1) {
        throw new Error("Too many users found");
    }

    const user = users.docs[0].data();
    return user;
};

export const getContactsByTagId = async (
    userId: string,
    tagId: string
): string[] => {
    const contactsQuery = query(
        collection(db, "tags_contact"),
        where("tagID", "==", tagId)
    );
    const users = await getDocs(contactsQuery);
    return users.docs.map((contact) => contact.data().contactID);
};

export const createEmptyTag = async (
    description: string,
    userId: string
): ContactTag => {
    const tag = {
        description: description,
        userID: userId,
    };
    const newTagRef = await addDoc(collection(db, "tags"), tag);
    const tagData = await getDoc(doc(db, "tags", newTagRef.id));
    if (!tagData.exists()) throw new Error("Could not create tag");
    return {
        ...tagData.data(),
        tag_id: newTagRef.id,
    };
};

export const addTagToContact = async (
    tagId: string,
    contactId: string,
    userId: string
): null => {
    const contactTag = {
        contactID: contactId,
        tagID: tagId,
        userID: userId,
    };
    const newContactTagRef = await addDoc(
        collection(db, "tags_contact"),
        contactTag
    );
    return null;
};

export const removeTagFromContact = async (
    tagId: string,
    contactId: string,
    userId: string
): null => {
    const tagQuery = query(
        collection(db, "tags_contact"),
        where("tagID", "==", tagId),
        where("contactID", "==", contactId),
        where("userID", "==", userId)
    );
    const querySnapshot = await getDocs(tagQuery);
    const contactTagToDelete = querySnapshot.docs[0].id;
    await deleteDoc(doc(db, "tags_contact", contactTagToDelete));
    return null;
};

export const createEmptyContact = async (
    data?: Partial<ContactMaster>
): ContactMaster => {
    const created_date = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");
    const contact = {
        ...CONTACT_MASTER,
        ...data,
        created_date: created_date,
        updated_date: created_date,
    };
    delete contact.contact_id;
    const newContactRef = await addDoc(
        collection(db, "contact_master"),
        contact
    );
    const contactData = await getDoc(
        doc(db, "contact_master", newContactRef.id)
    );
    if (!contactData.exists()) throw new Error("Could not create contact");
    return { ...contactData.data(), contact_id: contactData.id };
};

export const saveContact = async (
    contact: ContactMaster,
    person_profile?: File,
    dontUpdateDate?: boolean
): ContactMaster => {
    if (person_profile) {
        const person_profile_url = await saveContactPicture(
            contact.contact_id,
            person_profile
        );
        contact.person_profile = person_profile_url;
    }
    if (!dontUpdateDate)
        contact.updated_date = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");

    const myDoc = doc(db, "contact_master", contact.contact_id);
    const savedContactRef = await setDoc(myDoc, contact, { merge: true });
    const contactData = await getDoc(
        doc(db, "contact_master", contact.contact_id)
    );
    if (!contactData.exists()) throw new Error("Could not save contact");
    return contactData.data();
};

export const createEmptyPersonalFact = async (): ContactDescription => {
    const description = {
        ...CONTACT_DESCRIPTION,
        sharedFact: true,
    };
    delete description.desc_id;
    const newContactDescriptionRef = await addDoc(
        collection(db, "personal_shared_facts"),
        description
    );
    const contactDescriptionData = await getDoc(
        doc(db, "personal_shared_facts", newContactDescriptionRef.id)
    );
    if (!contactDescriptionData.exists())
        throw new Error("Could not create contact description");
    return {
        ...contactDescriptionData.data(),
        desc_id: contactDescriptionData.id,
    };
};

export const shareContact = async (userId: string, contactId: string) => {
    const checkIfExistsQuery = query(
        collection(db, "peer_sharing"),
        where("recipient_id", "==", userId),
        where("owner_id", "==", contactId)
    );
    const peerSharingRef = await getDocs(checkIfExistsQuery);
    if (peerSharingRef.docs.length === 0) {
        const peerSharing = await addDoc(collection(db, "peer_sharing"), {
            owner_id: contactId,
            recipient_id: userId,
        });
    }
    const checkIfContactExistsQuery = query(
        collection(db, "contact_master"),
        where("user_id", "==", userId),
        where("original_person_id", "==", contactId)
    );
    const checkIfContactExistsRef = await getDocs(checkIfContactExistsQuery);
    if (checkIfContactExistsRef.docs.length === 0) {
        const newContact = await createEmptyContact();
        const contactRef = await getDoc(doc(db, "user_master", contactId));
        if (!contactRef.exists()) return;
        const contact = contactRef.data();
        return await saveContact({
            ...newContact,
            name: contact.name,
            original_person_id: contactId,
            person_profile: contact.profile,
            shared_person: true,
            user_id: userId,
        });
    }
};

export const createEmptyContactDescription = async (): ContactDescription => {
    const description = {
        ...CONTACT_DESCRIPTION,
    };
    delete description.desc_id;
    const newContactDescriptionRef = await addDoc(
        collection(db, "contact_description_master"),
        description
    );
    const contactDescriptionData = await getDoc(
        doc(db, "contact_description_master", newContactDescriptionRef.id)
    );
    if (!contactDescriptionData.exists())
        throw new Error("Could not create contact description");
    return {
        ...contactDescriptionData.data(),
        desc_id: contactDescriptionData.id,
    };
};

export const updateContactUpdatedDate = async (contactId: string) => {
    const contactQuery = doc(db, "contact_master", contactId);
    const contactSnapshot = await getDoc(contactQuery);
    if (!contactSnapshot.exists()) {
        throw new Error("Could not update contact");
    }
    const contact = {
        ...contactSnapshot.data(),
        contact_id: contactId,
        updated_date: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
    };
    const updatedContact = saveContact(contact);
    return updatedContact;
};
export const savePersonalFact = async (
    description: ContactDescription,
    userId: string,
    profilePersonImage?: File
): ContactDescription => {
    description.user_id = userId;
    description.contact_id = userId;
    const created_date = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");
    if (!description.created_date) description.created_date = created_date;
    description.updated_date = created_date;
    if (profilePersonImage) {
        const person_profile_url = await saveContactPicture(
            description.desc_id,
            profilePersonImage
        );
        description.person_profile = person_profile_url;
    }
    description.sharedProfile = true;
    const myDoc = doc(db, "personal_shared_facts", description.desc_id);
    const savedContactDescription = await setDoc(myDoc, description, {
        merge: true,
    });
    const contactDescriptionData = await getDoc(
        doc(db, "personal_shared_facts", description.desc_id)
    );
    if (!contactDescriptionData.exists())
        throw new Error("Could not save contact description");
    return contactDescriptionData.data();
};

export const saveContactDescription = async (
    description: ContactDescription,
    userId: string,
    profilePersonImage?: File
): ContactDescription => {
    description.user_id = userId;
    const created_date = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");
    if (!description.created_date) description.created_date = created_date;
    description.updated_date = created_date;
    if (profilePersonImage) {
        const person_profile_url = await saveContactPicture(
            description.desc_id,
            profilePersonImage
        );
        description.person_profile = person_profile_url;
    }
    const myDoc = doc(db, "contact_description_master", description.desc_id);
    const savedContactDescription = await setDoc(myDoc, description, {
        merge: true,
    });
    const contactDescriptionData = await getDoc(
        doc(db, "contact_description_master", description.desc_id)
    );
    if (!contactDescriptionData.exists())
        throw new Error("Could not save contact description");
    updateContactUpdatedDate(description.contact_id);
    return contactDescriptionData.data();
};

export const saveContactPicture = async (contactId: string, file: File) => {
    const fileExtenstionSplit = file.name.split(".");
    const fileExtenstion = fileExtenstionSplit[fileExtenstionSplit.length - 1];
    const pictureRef = ref(storage, `profile/${contactId}.${fileExtenstion}`);
    const result = await uploadBytes(pictureRef, file);
    return `${contactId}.${fileExtenstion}`;
};

export const deleteContact = async (contact: ContactMaster): ContactMaster => {
    await deleteDoc(doc(db, "contact_master", contact.contact_id));
    if (contact.original_person_id) {
        const peerSharingRef = await getDocs(
            query(
                collection(db, "peer_sharing"),
                where("recipient_id", "==", contact.user_id),
                where("owner_id", "==", contact.original_person_id)
            )
        );
        if (peerSharingRef.docs.length === 1) {
            await deleteDoc(doc(db, "peer_sharing", peerSharingRef.docs[0].id));
        }
    }
    return contact;
};

export const removeRecipientContact = async (
    userId: string,
    recipientId: string
) => {
    const peerSharingRef = await getDocs(
        query(
            collection(db, "peer_sharing"),
            where("recipient_id", "==", recipientId),
            where("owner_id", "==", userId)
        )
    );
    for (
        let peerSharingIndex = 0;
        peerSharingIndex < peerSharingRef.docs.length;
        peerSharingIndex++
    ) {
        await deleteDoc(
            doc(db, "peer_sharing", peerSharingRef.docs[peerSharingIndex].id)
        );
    }
    const contactRef = await getDocs(
        query(
            collection(db, "contact_master"),
            where("user_id", "==", recipientId),
            where("original_person_id", "==", userId)
        )
    );
    if (contactRef.docs.length === 1) {
        await deleteDoc(doc(db, "contact_master", contactRef.docs[0].id));
    }
    return;
};

export const deletePersonalFact = async (fact: ContactDescription): null => {
    await deleteDoc(doc(db, "personal_shared_facts", fact.desc_id));
    return null;
};

export const deleteFact = async (
    fact: ContactDescription
): ContactDescription => {
    await deleteDoc(doc(db, "contact_description_master", fact.desc_id));
    return fact;
};

export const getAllUsers = async (email) => {
    const allUsersQuery = query(collection(db, "user_master"));
    const usersSnapshot = await getDocs(allUsersQuery);
    const users = usersSnapshot.docs.map((user) => user.data());
    return users;
};

export const getContactDescription = async (
    contactId: string
): ContactDescription[] => {
    const contactDescriptionQuery = query(
        collection(db, "contact_description_master"),
        where("contact_id", "==", contactId)
    );
    const contactDescriptionSnap = await getDocs(contactDescriptionQuery);
    return contactDescriptionSnap.docs.map((description) => description.data());
};

export const getContactDescriptionListener = (
    contactId: string,
    callback: (contactDescriptions: ContactDescription[]) => void
): (() => void) => {
    const contactDescriptionQuery = query(
        collection(db, "contact_description_master"),
        where("contact_id", "==", contactId)
    );
    const unsubscribe = onSnapshot(contactDescriptionQuery, (querySnapshot) => {
        const contactDescriptions = querySnapshot.docs.map((description) => ({
            ...description.data(),
            desc_id: description.id,
        }));
        callback(contactDescriptions);
    });
    return unsubscribe;
};

export const getSharedContactDescriptionListener = (
    contactId: string | null,
    callback: (contactDescriptions: ContactDescription[]) => void
): (() => void) => {
    const sharedContactDescriptionQuery = query(
        collection(db, "personal_shared_facts"),
        where("contact_id", "==", contactId)
    );
    if (!contactId) {
        return () => {};
    }
    const unsubscribe = onSnapshot(
        sharedContactDescriptionQuery,
        (querySnapshot) => {
            const contactDescriptions = querySnapshot.docs.map(
                (description) => ({
                    ...description.data(),
                    desc_id: description.id,
                })
            );
            callback(contactDescriptions);
        }
    );
    return unsubscribe;
};

const getTagsById = async (tagIds: string[]): ContactTag[] => {
    const tags = [];
    for (
        let batchIndex = 0;
        batchIndex < Math.floor((tagIds.length + 9) / 10);
        batchIndex++
    ) {
        const batchIds = tagIds.filter(
            (id: string, index: number) => Math.floor(index / 10) === batchIndex
        );
        const batchQuery = query(
            collection(db, "tags"),
            where(documentId(), "in", batchIds)
        );
        const batchTags = await getDocs(batchQuery);
        const batchData = batchTags.docs.map((tag) => ({
            ...tag.data(),
            tag_id: tag.id,
        }));
        tags.push(...batchData);
    }
    return tags;
};

export const _getAllTags = async () => {
    const tagsQuery = query(collection(db, "tags"));
    const tagsSnapshot = await getDocs(tagsQuery);
    const tagsData = tagsSnapshot.docs.map((x) => ({
        ...x.data(),
        tag_id: x.id,
    }));
    return tagsData;
};

export const getContactTagsListener = (
    contactId: string,
    callback: (tags: ContactTag[]) => void
): (() => void) => {
    const contactTagsQuery = query(
        collection(db, "tags_contact"),
        where("contactID", "==", contactId)
    );

    const unsubscribe = onSnapshot(contactTagsQuery, async (querySnapshot) => {
        const tagIds = querySnapshot.docs.map((tag) => tag.data().tagID);
        const tags = await getTagsById(tagIds);
        callback(tags);
    });
    return unsubscribe;
};
